import { VehicleReplacementDto } from 'api/vehicles/models/VehicleReplacementDto';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import Button from 'common/components/button/Button';
import Label from 'common/components/label/Label';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useToasts } from 'react-toast-notifications';
import styles from './VehicleReplacementModal.module.scss';
import CloseIcon from 'assets/svg/mobile_close.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import SelectController from 'common/components/select/SelectController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { Col, Row } from 'react-flexbox-grid';
import DateTimePickerControllerNew from 'common/components/dateTimePicker/DateTimePickerControllerNew';
import FormItem from 'common/components/formItem/FormItem';
import InputResizableController from 'common/components/input/InputResizableController';
import VehiclesService from 'api/vehicles/VehiclesService';
import InputError from 'common/components/inputError/InputError';
import ReactTooltip from 'react-tooltip';
import { VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import { FaInfoCircle } from 'react-icons/fa';

type Props = {
    isVisible: boolean;
    item?: VehicleReplacementDto;
    isEdit?: boolean;
    replacedVehicle?: string;
    currentVehicle: VehicleViewModel;
    verificationId?: string | null;
    onSave: () => void;
    onCancel: () => void;
};

const VehicleReplacementModal: React.FC<Props> = ({ isVisible, item, isEdit, replacedVehicle, currentVehicle, verificationId, onSave, onCancel }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const form = useForm<VehicleReplacementDto>({ shouldUnregister: false, defaultValues: { replacementStartDate: new Date() } });
    const { handleSubmit, reset, errors } = form;

    const [vehicleOptions, setVehicleOptions] = useState<SelectValueLabel[]>([]);
    const [hasVerificationId, setHasVerificationId] = useState(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const reasonFieldRef = useRef<HTMLDivElement>(null);

    const isFinishReplace = Boolean(replacedVehicle && !isEdit);
    const showBothDates = isFinishReplace || (isEdit && item?.replacementEndDate);
    const isSmallScreen = windowHeight < 800;

    console.log('');

    const customStyles = {
        content: {
            top: isSmallScreen ? '17%' : '20%',
            left: '20%',
            right: '20%',
            bottom: isSmallScreen ? '18%' : '20%',
            backgroundClip: 'padding-box',
            backgroundColor: '#FFFFFF',
            border: '1px solid transparent',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            outline: '0 none',
            padding: '20px'
        },
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
    }

    const getData = async () => {
        try {
            Loading.show();
            // Passa o vehicleId para mostrar o veículo substituto atual mesmo que esteja inativo ou a substituir outro
            const vehicleIdToInclude = (isEdit || isFinishReplace) ? item?.replacementVehicleId ?? currentVehicle.replacementVehicleId : undefined;
            const vehicles = await VehiclesService.getVehiclesToReplacement({ vehicleId: vehicleIdToInclude });
            // Filtrar para excluir o veículo atual da lista

            const filteredVehicles = (isEdit || isFinishReplace) ? vehicles : vehicles.filter(v => v.value !== currentVehicle.id);
            setVehicleOptions(filteredVehicles);

            if (item) {
                // Modo edição - preencher com dados do item
                reset({
                    id: item.id,
                    vehicleId: item.vehicleId,
                    replacementVehicleId: item.replacementVehicleId,
                    replacementStartDate: item.replacementStartDate ? new Date(item.replacementStartDate) : new Date(),
                    replacementEndDate: item.replacementEndDate ? new Date(item.replacementEndDate) : null,
                    reason: item.reason || ''
                });
                setHasVerificationId(Boolean(item.verificationId));
            } else if (replacedVehicle && currentVehicle.id) {
                const replacementItem = await VehiclesService.getReplacementByVehicleId(currentVehicle.id);
                if (replacementItem) {
                    reset({
                        id: replacementItem.id,
                        vehicleId: replacementItem.vehicleId,
                        replacementVehicleId: replacementItem.replacementVehicleId,
                        replacementStartDate: replacementItem.replacementStartDate ? new Date(replacementItem.replacementStartDate) : new Date(),
                        replacementEndDate: replacementItem.replacementEndDate ? new Date(replacementItem.replacementEndDate) : null,
                        reason: replacementItem.reason || ''
                    });
                    setHasVerificationId(Boolean(replacementItem.verificationId));
                }
            }

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicle information `, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const onSubmit = async (formData: VehicleReplacementDto) => {
        try {
            if (!currentVehicle.id) { return; }

            Loading.show();

            if (isEdit) {
                // Editar substituição existente
                formData.vehicleId = currentVehicle.id;
                await VehiclesService.updateReplacement(formData);
                addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            }
            else if (replacedVehicle) {
                // Terminar substituição
                await VehiclesService.finishReplacement(formData);
                addToast(t('replacement_vehicles.finish_replacement_success'), { appearance: 'success' });
            } else {
                // Criar nova substituição
                formData.vehicleId = currentVehicle.id;
                formData.verificationId = verificationId;
                await VehiclesService.createReplacement(formData);
                addToast(t('replacement_vehicles.create_replacement_success'), { appearance: 'success' });
            }
            onSave();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Error managing vehicle replacement`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }

    const onError = async () => {
        addToast(t('common.messages.validate_form'), { appearance: 'warning' });
    }

    const onClickSaveAndDeactivate = async (formData: VehicleReplacementDto) => {
        try {
            Loading.show();

            // Terminar substituição primeiro
            await VehiclesService.finishReplacement(formData);
            addToast(t('replacement_vehicles.finish_replacement_success'), { appearance: 'success' });

            Loading.hide();

            // Desativa o veículo substituto 
            if (currentVehicle.replacementVehicleId) {
                deactivateReplacementVehicle();
            } else {
                onSave();
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Error finishing replacement`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Loading.hide();
        }
    }

    const deactivateReplacementVehicle = async () => {
        try {
            if (!currentVehicle.replacementVehicleId) { return; }

            Loading.show();
            // Desativa o veículo substituto
            await VehiclesService.remove({ id: currentVehicle.replacementVehicleId } as VehicleDto);
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });

            onSave();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Error deactivating vehicle`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        if (isVisible) {
            getData();
        } else {
            reset({
                replacementVehicleId: '',
                replacementStartDate: new Date(),
                replacementEndDate: null,
                reason: ''
            });
            setHasVerificationId(false);
        }
    }, [isVisible]);

    useEffect(() => {
        if (errors?.reason) {
            reasonFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errors?.reason]);

    useEffect(() => {
        const handleResize = () => setWindowHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const openCreateVehicle = () => {
        const newWindow = window.open('/vehicles/new', '_blank');

        const handleFocus = () => {
            getData();
            window.removeEventListener('focus', handleFocus);
        };

        if (newWindow) {
            window.addEventListener('focus', handleFocus);
        }
    };

    return (
        <Modal isOpen={isVisible} style={customStyles}>

            <form onSubmit={handleSubmit(onSubmit, onError)} className={styles.formContainer}>
                <div className={styles.header}>
                    <Label className={styles.textTitle}>
                        {isEdit ? t('replacement_vehicles.edit') : replacedVehicle ? t('replacement_vehicles.finish_replacement') : t('replacement_vehicles.replace')}</Label>
                    <div>
                        <img src={CloseIcon} onClick={() => onCancel()} className={styles.closeButton} />
                    </div>
                </div>

                <div className={styles.content}>
                    <Row>
                        <Col xs={showBothDates ? 12 : 6}>
                            <FormItem>
                                <div style={{ display: 'flex' }}>
                                    <Label className={styles.label} required={true}>{t('vehicle.vehicle')}</Label>

                                    <FaInfoCircle data-tip={t('replacement_vehicles.only_vehicles_available')} style={{ marginLeft: '5px' }} />
                                    <ReactTooltip />
                                </div>

                                {!isFinishReplace ? (
                                    <div className={styles.selectWithButton}>
                                        <div className={styles.selectContainer}>
                                            <SelectController
                                                form={form as any}
                                                isDisabled={false}
                                                placeholder={t('vehicle.vehicle')}
                                                name="replacementVehicleId"
                                                options={vehicleOptions}
                                                rules={{ required: true }} />
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.addButton}
                                            onClick={openCreateVehicle}
                                            data-tip={t('replacement_vehicles.add_new')}
                                        >
                                            <img src={AddIcon} />
                                        </button>
                                        <ReactTooltip />
                                    </div>
                                ) : (
                                        <SelectController
                                            form={form as any}
                                            isDisabled={true}
                                            placeholder={t('vehicle.vehicle')}
                                            name="replacementVehicleId"
                                            options={vehicleOptions}
                                            rules={{ required: true }} />
                                    )}
                                <InputError error={errors.replacementVehicleId} />
                            </FormItem>
                        </Col>
                        {!showBothDates && (
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.label} required={true}>{t('replacement_vehicles.replacement_start_date')}</Label>
                                    <DateTimePickerControllerNew
                                        form={form}
                                        placeholderText={t('replacement_vehicles.replacement_start_date')}
                                        name={'replacementStartDate'}
                                        disabled={false}
                                        rules={{ required: true }} />
                                    <InputError error={errors?.replacementStartDate} />
                                </FormItem>
                            </Col>
                        )}
                    </Row>

                    {showBothDates && (
                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.label} required={true}>{t('replacement_vehicles.replacement_start_date')}</Label>
                                    <DateTimePickerControllerNew
                                        form={form}
                                        placeholderText={t('replacement_vehicles.replacement_start_date')}
                                        name={'replacementStartDate'}
                                        disabled={isFinishReplace}
                                        rules={{ required: true }}
                                        popperProps={{ strategy: 'fixed', positionFixed: true } as any} />
                                    <InputError error={errors?.replacementStartDate} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.label} required={!isEdit ? true : false}>{t('replacement_vehicles.replacement_end_date')}</Label>
                                    <DateTimePickerControllerNew
                                        form={form}
                                        placeholderText={t('replacement_vehicles.replacement_end_date')}
                                        name={'replacementEndDate'}
                                        minDate={form.watch('replacementStartDate')}
                                        rules={{ required: !isEdit ? true : false }}
                                        popperProps={{ strategy: 'fixed', positionFixed: true } as any} />
                                    <InputError error={errors?.replacementEndDate} />
                                </FormItem>
                            </Col>
                        </Row>
                    )}

                    {/* Linha 3: Motivo (sempre visível) */}
                    <Row>
                        <Col xs={12}>
                            <FormItem>
                                <Label className={styles.label}>{t('replacement_vehicles.replacement_reason')}</Label>
                                <InputResizableController
                                    form={form as any}
                                    name={'reason'}
                                    placeholder={t('replacement_vehicles.replacement_reason')}
                                    disabled={isFinishReplace}
                                    rules={{ maxLength: 500 }}
                                    rows={3} />
                                <div ref={reasonFieldRef}>
                                    <InputError error={errors?.reason} maxLength={500} />
                                </div>
                            </FormItem>
                        </Col>
                    </Row>
                </div>

                {(isFinishReplace && currentVehicle?.active && hasVerificationId) ?
                    <div className={styles.buttonsContainer} style={{ justifyContent: 'space-between' }}>
                        <div>
                            <Button text={t('common.cancel')} preset={'secondary'} size={'normal'} onClick={onCancel} />
                        </div>
                        <div>
                            <Button text={t('replacement_vehicles.finish_and_deactivate_replacement')} preset={'secondary'} size={'normal'} type="button" onClick={handleSubmit(onClickSaveAndDeactivate, onError)} />
                            <Button text={t('common.save')} size={'normal'} type="button" onClick={handleSubmit(onSubmit, onError)} />
                        </div>
                    </div>
                    :
                    <div className={styles.buttonsContainer}>
                        <div>
                            <Button text={t('common.cancel')} preset={'secondary'} size={'normal'} onClick={onCancel} />
                            <Button text={t('common.save')} size={'normal'} type="button" onClick={handleSubmit(onSubmit, onError)} />
                        </div>
                    </div>
                }
            </form>
        </Modal>
    );
}

export default VehicleReplacementModal;